import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../IntroducedRegistration/IntroducedInvestorRegistration.scss';
import { CompactHeader } from '../../../components/CompactHeader/CompactHeader';
import { CompactFooter } from '../../../components/CompactFooter/CompactFooter';
import { FiCheck } from 'react-icons/fi';

const CheckIcon = FiCheck as any;

/**
 * Success Page after self-registration — introduced-flow parity (2026-09-05).
 *
 * When the completion response carried an azureUserId (B2C user created), the primary
 * action is "Set Your Password" straight into /investor/setpassword/{id} — the emailed
 * link points at that same page, so making the investor go via their inbox was pure
 * friction. "Go to Login" is NOT offered then: the account has no password yet, so it
 * was a guaranteed dead end (same reasoning as IntroducedInvestorSuccess).
 *
 * Without an azureUserId (B2C creation failed, or the page was refreshed and the
 * navigation state was lost) we fall back to the email-based copy with Login/Home.
 */
export const SelfRegistrationSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const azureUserId = (location.state as { azureUserId?: string } | null)?.azureUserId;
  const canSetPassword = !!azureUserId;

  return (
    <>
      <CompactHeader />

      <section className="login-form-style4 section-padding py-12 flex justify-center items-center min-h-[60vh]">
        <div className="container flex justify-center">
          <div className="bg-white rounded-xl max-w-sm w-full p-6 shadow-xl border border-slate-100 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-4">
              <CheckIcon size={32} className="text-emerald-600 flex-shrink-0" />
            </div>

            <h3 className="text-[22px] font-bold text-slate-800 mb-2">Registration Successful!</h3>

            <div className="text-[12px] text-slate-500 mb-4 max-w-[280px] leading-relaxed">
              <p className="mb-2">Thank you for registering with Facilon Services.</p>
              {canSetPassword ? (
                <>
                  <p className="mb-2">
                    One step left: <strong>set your password</strong> to activate your account.
                  </p>
                  <p className="mb-0">
                    You can do it right now, or later via the email we sent you titled{' '}
                    <strong>&ldquo;Facilon &ndash; Set Your Password&rdquo;</strong> (check spam if
                    it isn&rsquo;t in your inbox).
                  </p>
                </>
              ) : (
                <>
                  <p className="mb-2">Your registration has been submitted successfully.</p>
                  <p className="mb-2">We've sent you an email with a secure link to set your password and activate your account.</p>
                  <p className="mb-0">Please check your inbox (and spam folder) and click the link to continue.</p>
                </>
              )}
            </div>

            <div className="w-full flex flex-col gap-2">
              {canSetPassword ? (
                <button
                  type="button"
                  className="w-full py-2 bg-[#3e6f7c] hover:bg-[#355f69] text-white text-[12px] font-bold rounded-md transition-colors cursor-pointer border-0 shadow-sm"
                  onClick={() => navigate(`/investor/setpassword/${azureUserId}`)}
                >
                  Set Your Password
                </button>
              ) : (
                <button
                  type="button"
                  className="w-full py-2 bg-[#3e6f7c] hover:bg-[#355f69] text-white text-[12px] font-bold rounded-md transition-colors cursor-pointer border-0 shadow-sm"
                  onClick={() => navigate('/login')}
                >
                  Go to Login
                </button>
              )}
              <button
                type="button"
                className="w-full py-2 bg-[#f8fafc] hover:bg-slate-100 text-slate-600 text-[12px] font-bold rounded-md transition-colors cursor-pointer border border-slate-200 shadow-sm"
                onClick={() => navigate('/')}
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </section>
      <CompactFooter />
    </>
  );
};

export default SelfRegistrationSuccess;
